/**
 * Pivot league standings history columns into per-user point series for the
 * standings-over-time chart.
 *
 * Source columns come from leagues/{id}/history/{matchday} (see models/leagueHistory.ts):
 * parallel `order` (uid rank order) and `scores` (cumulative points) arrays.
 *
 * Output series are indexed by matchday 0..maxMatchday, where index 0 is a shared
 * baseline of 0 so every line fans out from the same origin. A `null` entry means the
 * user had no settled standing for that matchday (e.g. a late joiner before they appear).
 */
import type { Dict } from '@/helpers';
import type { LeagueHistoryColumn } from '@/models/leagueHistory';
import type { leagueMember } from '@/models/user';

export interface UserSeries {
  uid: string;
  name: string;
  /** Cumulative points per matchday, indexed 0..maxMatchday. index 0 === 0. */
  points: (number | null)[];
  /** Most recent settled matchday at which this user had a standing. */
  lastMatchday: number;
  /** Final cumulative score (points at lastMatchday), used for ordering/colors. */
  finalScore: number;
}

export interface StandingsSeries {
  /** Highest matchday present across all columns. */
  maxMatchday: number;
  /** One series per user, ordered by finalScore desc (rank 1 first). */
  series: UserSeries[];
}

const UNKNOWN_NAME = 'Player';

function resolveName(uid: string, membersByUid: Dict<leagueMember> | undefined): string {
  const member = membersByUid?.[uid];
  const name = member?.name?.trim();
  return name && name.length > 0 ? name : UNKNOWN_NAME;
}

/**
 * Build per-user point series from sorted history columns.
 *
 * @param columns history columns (any order; sorted internally by matchday)
 * @param membersByUid leaderboard cache members for display names (optional)
 */
export function buildStandingsSeries(
  columns: LeagueHistoryColumn[],
  membersByUid?: Dict<leagueMember>,
): StandingsSeries {
  const sorted = [...columns].sort((a, b) => a.matchday - b.matchday);
  const maxMatchday = sorted.reduce((max, col) => Math.max(max, col.matchday), 0);

  // Collect score-by-matchday for every uid seen in any column.
  const byUid = new Map<string, Map<number, number>>();
  for (const col of sorted) {
    for (let i = 0; i < col.order.length; i += 1) {
      const uid = col.order[i];
      const score = col.scores[i] ?? 0;
      if (!byUid.has(uid)) {
        byUid.set(uid, new Map());
      }
      byUid.get(uid)!.set(col.matchday, score);
    }
  }

  const series: UserSeries[] = [];
  for (const [uid, scoreByMd] of byUid) {
    const points: (number | null)[] = new Array(maxMatchday + 1).fill(null);
    // Anchor at the shared (0,0) origin only for members present from matchday 1.
    // Late joiners start their line at their first settled matchday instead.
    if (scoreByMd.has(1)) {
      points[0] = 0;
    }
    let lastMatchday = 0;
    let finalScore = 0;
    for (let md = 1; md <= maxMatchday; md += 1) {
      const score = scoreByMd.get(md);
      if (score !== undefined) {
        points[md] = score;
        lastMatchday = md;
        finalScore = score;
      }
    }
    series.push({ uid, name: resolveName(uid, membersByUid), points, lastMatchday, finalScore });
  }

  series.sort((a, b) => b.finalScore - a.finalScore || a.name.localeCompare(b.name));

  return { maxMatchday, series };
}

/** Points a user gained on matchday `k` (k >= 1). Returns null if either endpoint is unknown. */
export function matchdayDelta(series: UserSeries, k: number): number | null {
  if (k < 1 || k >= series.points.length) return null;
  const curr = series.points[k];
  const prev = series.points[k - 1];
  if (curr == null || prev == null) return null;
  return curr - prev;
}

/** Rank (1-based) of a uid at matchday `k`, or null if not present in that column. */
export function rankAt(columns: LeagueHistoryColumn[], k: number, uid: string): number | null {
  const col = columns.find((c) => c.matchday === k);
  if (!col) return null;
  const idx = col.order.indexOf(uid);
  return idx === -1 ? null : idx + 1;
}
